import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';

const fmt = (s) => {
  if (!s && s !== 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Renders a real waveform from the audio file and exposes a draggable region
// for selecting [trimStart, trimEnd]. Calls onChange({ trimStart, trimEnd })
// and onDuration(seconds) once decoded.
export default function WaveformEditor({
  url,
  trimStart = 0,
  trimEnd = 0,
  onChange,
  onDuration,
}) {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const regionRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !url) return undefined;
    setReady(false);
    setError(null);

    const regions = RegionsPlugin.create();
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#5a5a5a',
      progressColor: '#e50914',
      cursorColor: '#fff',
      height: 96,
      barWidth: 2,
      barGap: 1,
      url,
      plugins: [regions],
    });
    wsRef.current = ws;

    ws.on('decode', (duration) => {
      onDuration?.(duration);
      const start = Math.min(trimStart || 0, duration);
      const end = trimEnd && trimEnd > start ? Math.min(trimEnd, duration) : duration;
      const region = regions.addRegion({
        start,
        end,
        color: 'rgba(229, 9, 20, 0.18)',
        drag: true,
        resize: true,
      });
      regionRef.current = region;
      setReady(true);
    });

    regions.on('region-updated', (region) => {
      onChange?.({ trimStart: region.start, trimEnd: region.end });
    });

    ws.on('error', (e) => setError(String(e?.message || e)));
    ws.on('finish', () => setPlaying(false));
    ws.on('pause', () => setPlaying(false));
    ws.on('play', () => setPlaying(true));

    return () => {
      ws.destroy();
      wsRef.current = null;
      regionRef.current = null;
    };
    // Intentionally only re-init when the source URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const playRegion = () => {
    const ws = wsRef.current;
    const region = regionRef.current;
    if (!ws || !region) return;
    if (playing) {
      ws.pause();
    } else {
      region.play();
    }
  };

  return (
    <div>
      {error ? (
        <div className="cs-empty">Couldn’t load waveform: {error}</div>
      ) : (
        <>
          <div
            ref={containerRef}
            style={{ background: '#111', borderRadius: 8, padding: 8, minHeight: 112 }}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
            <button
              type="button"
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={playRegion}
              disabled={!ready}
            >
              {playing ? '❚❚ Pause' : '▶ Play selection'}
            </button>
            <span style={{ color: 'var(--muted)', fontSize: 13 }}>
              {ready
                ? `Selection: ${fmt(trimStart)} – ${fmt(trimEnd)} (drag edges to trim)`
                : 'Decoding audio…'}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
